import { request } from "./client";

export type TeamAction =
  | "create"
  | "description"
  | "logo"
  | "banner"
  | "sponsor"
  | "base"
  | "import"
  | "edit"
  | "invite"
  | "kick"
  | "transfer"
  | "leave"
  | "archive"
  | "sync";

/** SteamID64 values must remain strings throughout the request and response. */
export type TeamCommand = {
  action?: TeamAction;
  bot_id?: string;
  team_id?: number;
  steam_id?: string;
  name?: string;
  tag?: string;
  abbreviation?: string;
  country_code?: string;
  url?: string;
  expected_revision?: number;
  description?: string;
  image_data?: string;
};

export type ManagedTeam = {
  description?: string;
  team_id: number;
  bot_id: string;
  snapshot: {
    bot_steam_id: string;
    team_id: number;
    name: string;
    tag: string;
    abbreviation: string;
    country_code: string;
    url: string;
    logo_id?: string;
    banner_id?: string;
    sponsor_id?: string;
    base_id?: string;
    logo_url?: string;
    admin_steam_id: string;
    members: Array<{ steam_id: string; admin: boolean }>;
  };
  revision: number;
  status: string;
  updated_at: string;
};

export type TeamOperationDiagnostics = {
  stage: string;
  reason?: string;
  detail?: string;
  error_type?: string;
  gc_acknowledged: boolean;
  create_elapsed_ms: number;
  confirmation_elapsed_ms: number;
  confirmation_budget_ms?: number | null;
  expected_team_id?: number;
  observed_team_id?: number;
  observed_member_count: number;
  mismatches?: Array<{
    field: string;
    expected: string;
    observed: string;
  }> | null;
  next_action?: string;
};

export type TeamOperation = {
  diagnostics?: TeamOperationDiagnostics | null;
  operation_id: string;
  bot_id: string;
  actor: string;
  action: string;
  status: string;
  code?: string;
  message?: string;
  team_id?: number;
  command: TeamCommand & { bot_steam_id?: string };
  gc_result?: number;
  invitation_pending: boolean;
  created_at: string;
  updated_at: string;
};

export type TeamCapacity = {
  bot_id: string;
  policy_max_managed_teams: number | null;
  managed_teams: number;
  reserved_new_teams: number;
  membership_discovery_complete: boolean;
  verified_remote_membership_limit: number | null;
  remote_slots_available: number | null;
  can_create: boolean;
  block_reasons: string[];
};

type Page = { limit: number; offset: number };

export type TeamBot = {
  bot_id: string;
  username: string;
  profile_name?: string;
  status: string;
  purpose: string;
};

export const proTeams = {
  async bots(): Promise<TeamBot[]> {
    const page = await request<{ accounts: TeamBot[] | null }>(
      "/api/v1/pool/status",
    );
    return (page.accounts ?? []).filter(
      (bot) =>
        !!bot.bot_id &&
        [undefined, "", "lobby", "teams", "both"].includes(bot.purpose),
    );
  },
  async list(limit = 50, offset = 0): Promise<Page & { teams: ManagedTeam[] }> {
    const page = await request<Page & { teams: ManagedTeam[] | null }>(
      "/api/v1/teams",
      { query: { limit, offset } },
    );
    return { ...page, teams: page.teams ?? [] };
  },
  get: (teamId: number) => request<ManagedTeam>(`/api/v1/teams/${teamId}`),
  capacity: (botId: string) =>
    request<TeamCapacity>(
      `/api/v1/pool/bot/${encodeURIComponent(botId)}/team-capacity`,
    ),
  identity: () => request<{ name: string; role: string }>("/api/v1/identity"),
  async operations(
    teamId: number,
    limit = 50,
    offset = 0,
  ): Promise<Page & { operations: TeamOperation[] }> {
    const page = await request<Page & { operations: TeamOperation[] | null }>(
      `/api/v1/teams/${teamId}/audit`,
      { query: { limit, offset } },
    );
    return { ...page, operations: page.operations ?? [] };
  },
  async invitations(
    teamId: number,
    limit = 50,
    offset = 0,
  ): Promise<Page & { invitations: TeamOperation[] }> {
    const page = await request<Page & { invitations: TeamOperation[] | null }>(
      `/api/v1/teams/${teamId}/invitations`,
      { query: { limit, offset } },
    );
    return { ...page, invitations: page.invitations ?? [] };
  },
  operation: (id: string) =>
    request<TeamOperation>(`/api/v1/team-operations/${encodeURIComponent(id)}`),
  recoverBot: (id: string) =>
    request<{ bot_id: string; quarantine_cleared: boolean; message: string }>(
      `/api/v1/pool/bot/${encodeURIComponent(id)}/team-recovery`,
      { method: "POST" },
    ),
  submit(
    action: TeamAction,
    teamId: number | undefined,
    body: TeamCommand,
    key: string,
  ): Promise<TeamOperation> {
    const path = `/api/v1/teams/${teamId}`;
    if (action === "create")
      return request("/api/v1/teams", {
        method: "POST",
        body,
        idempotencyKey: key,
      });
    if (action === "import")
      return request("/api/v1/teams/import", {
        method: "POST",
        body,
        idempotencyKey: key,
      });
    if (action === "edit" || action === "description")
      return request(action === "description" ? `${path}/description` : path, {
        method: "PATCH",
        body,
        idempotencyKey: key,
      });
    if (action === "kick") {
      const { steam_id, ...command } = body;
      return request(`${path}/members/${encodeURIComponent(steam_id ?? "")}`, {
        method: "DELETE",
        body: command,
        idempotencyKey: key,
      });
    }
    const suffix =
      action === "invite"
        ? "invitations"
        : action === "transfer"
          ? "admin-transfer"
          : action;
    return request(`${path}/${suffix}`, {
      method: "POST",
      body,
      idempotencyKey: key,
    });
  },
};
